import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { useConfigurations } from '@/features/admin/configurations/hooks'
import { useCreateAssignment, useNumberingSchemes } from '../hooks'
import type { DocumentType } from '../types'
import { DOCUMENT_TYPE_LABELS } from '../types'

const DOCUMENT_TYPE_OPTIONS: { value: DocumentType; label: string }[] = [
  { value: 'CONTRACT', label: DOCUMENT_TYPE_LABELS.CONTRACT },
  { value: 'INVOICE', label: DOCUMENT_TYPE_LABELS.INVOICE },
  { value: 'ANNEX', label: DOCUMENT_TYPE_LABELS.ANNEX },
  { value: 'OFFER', label: DOCUMENT_TYPE_LABELS.OFFER },
]

const WILDCARD_VALUE = '__wildcard__'

type Props = {
  onClose: () => void
}

export function AssignmentFormDialog({ onClose }: Props) {
  const create = useCreateAssignment()
  const schemesQ = useNumberingSchemes()
  const productsQ = useConfigurations(true)

  const [documentType, setDocumentType] = useState<DocumentType>('CONTRACT')
  const [configurationCode, setConfigurationCode] = useState<string>(WILDCARD_VALUE)
  const [schemeCode, setSchemeCode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const productOptions = [
    { value: WILDCARD_VALUE, label: 'Wszystkie (wildcard)' },
    ...(productsQ.data?.map(p => ({ value: p.code, label: `${p.labelPl} (${p.code})` })) ?? []),
  ]

  const schemeOptions = schemesQ.data
    ?.filter(s => s.isActive)
    .map(s => ({ value: s.code, label: `${s.label} (${s.code})` })) ?? []

  const submit = () => {
    setError(null)
    if (!schemeCode) { setError('Wybierz schemat numeracji.'); return }

    create.mutate(
      {
        documentType,
        configurationCode: configurationCode === WILDCARD_VALUE ? null : configurationCode,
        schemeCode,
      },
      { onSuccess: onClose, onError: () => setError('Błąd tworzenia przypisania - sprawdź czy takie już nie istnieje.') },
    )
  }

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[480px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">Nowe przypisanie schematu</Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            Przypisz schemat numeracji do typu dokumentu i produktu.
            Wildcard dotyczy wszystkich produktów bez jawnego przypisania.
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Typ dokumentu</span>
              <Combobox<DocumentType>
                value={documentType}
                onChange={v => v && setDocumentType(v)}
                options={DOCUMENT_TYPE_OPTIONS}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Produkt</span>
              <Combobox<string>
                value={configurationCode}
                onChange={v => v && setConfigurationCode(v)}
                options={productOptions}
                disabled={productsQ.isLoading}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Schemat numeracji</span>
              <Combobox<string>
                value={schemeCode}
                onChange={v => setSchemeCode(v)}
                options={schemeOptions}
                disabled={schemesQ.isLoading}
                placeholder="Wybierz schemat..."
              />
            </label>

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={create.isPending}>Anuluj</button>
              <button
                type="button"
                onClick={submit}
                className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50"
                disabled={create.isPending}
              >
                {create.isPending ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
